// Single source for the bookshelf's element-based themes. One compact descriptor per element (its picker
// swatch + the primitives each renderer needs) builds the three per-renderer registries (spine / cover /
// vinyl), so a theme like "fire" looks consistent whether an item shows as a book spine, a cover frame, or
// a vinyl record. Elements follow the brand's classical set — earth / air / fire / water / spirit (aether,
// purple + teal) — plus `gold` (the warm gilt formerly carried by spirit) and `mono` (B&W). `default` keeps
// the original Celtic look.
//
// Type-only imports of the renderer theme shapes (no runtime cycle: values flow element_themes -> renderers).
import type { BookSpineTheme } from "./book_spine"
import type { BookCoverTheme } from "./book_cover"
import type { SongVinylTheme } from "./song_vinyl"

// The ordered element set. `default` first so it leads the picker; the rest follow the elemental order.
export const ELEMENT_THEME_NAMES = [
  "default",
  "earth",
  "air",
  "fire",
  "water",
  "spirit",
  "gold",
  "illumination",
  "mono",
] as const

export type ElementThemeName = (typeof ELEMENT_THEME_NAMES)[number]

interface ElementCore {
  /** Human label for the picker + tooltip. */
  label: string
  /** A representative swatch colour for the picker (mirrors the element's brand hue). */
  swatch: string
  // Spine primitives
  spinePalette: string[]
  spineTrim: string
  spineLabel: string
  spineLabelClass?: string
  // Cover primitive: the frame ring/treatment (Tailwind classes). Undefined = no frame.
  coverFrame?: string
  // Vinyl primitives
  vinylDisc: string
  vinylLabels: string[]
  vinylGroove?: string
}

const DEFAULT_GROOVE = "rgba(255,255,255,0.08)"

// Tailwind bg utility per element for the image-less sleeve — the element's brand token, so a sleeve reads
// as the element's default colour (e.g. air = light teal, spirit = purple) rather than a seeded accent.
// Literal strings so Tailwind's JIT sees them. `default` is empty -> the sleeve keeps its seeded label hex.
const ELEMENT_BG: Record<ElementThemeName, string> = {
  default: "",
  earth: "bg-earth",
  air: "bg-air",
  fire: "bg-fire",
  water: "bg-water",
  spirit: "bg-spirit",
  gold: "bg-gold",
  illumination: "bg-illumination",
  mono: "bg-gray-900",
}

// The compact per-element source. Colours mirror the brand element hues (see app/assets/stylesheets/colors.css:
// earth=emerald, air=sky, fire=red, water=blue, spirit=violet, gold=amber) so the shelf reads on-brand.
const ELEMENTS: Record<ElementThemeName, ElementCore> = {
  default: {
    label: "Default",
    swatch: "#7c6f5a",
    spinePalette: ["#3f5c3a", "#5c3a3a", "#3a4a5c", "#5c503a", "#4a3a5c", "#2f4a45"],
    spineTrim: "var(--color-gold, #fcd34d)",
    spineLabel: "#ffffff",
    spineLabelClass: "font-serif tracking-wide",
    coverFrame: undefined,
    vinylDisc: "#181514",
    vinylLabels: ["#c9a23f", "#8a3b3b", "#2f4a45", "#3a4a5c", "#5c503a", "#4a3a5c"],
  },
  earth: {
    label: "Earth",
    swatch: "#059669",
    spinePalette: ["#065f46", "#047857", "#059669", "#0f766e", "#166534", "#14532d"],
    spineTrim: "var(--color-gold, #fcd34d)",
    spineLabel: "#ecfdf5",
    spineLabelClass: "font-serif tracking-wide",
    coverFrame: "rounded-sm ring-1 ring-earth/60",
    vinylDisc: "#0b1f18",
    vinylLabels: ["#10b981", "#34d399", "#059669", "#6ee7b7"],
  },
  air: {
    label: "Air",
    swatch: "#7dd3fc",
    spinePalette: ["#0ea5e9", "#38bdf8", "#0284c7", "#0369a1", "#075985", "#0c4a6e"],
    spineTrim: "#e0f2fe",
    spineLabel: "#f0f9ff",
    spineLabelClass: "font-sans tracking-wide",
    coverFrame: "rounded-sm ring-1 ring-air/70",
    vinylDisc: "#0e2230",
    vinylLabels: ["#38bdf8", "#7dd3fc", "#0ea5e9", "#bae6fd"],
  },
  fire: {
    label: "Fire",
    swatch: "#ef4444",
    spinePalette: ["#7f1d1d", "#991b1b", "#b91c1c", "#dc2626", "#9a3412", "#7c2d12"],
    spineTrim: "var(--color-gold, #fcd34d)",
    spineLabel: "#fee2e2",
    spineLabelClass: "font-serif tracking-wide",
    coverFrame: "rounded-sm ring-1 ring-fire/60 shadow-md",
    vinylDisc: "#1a0d0b",
    vinylLabels: ["#ef4444", "#f87171", "#fb923c", "#dc2626"],
  },
  water: {
    label: "Water",
    swatch: "#60a5fa",
    spinePalette: ["#1e3a8a", "#1d4ed8", "#2563eb", "#1e40af", "#155e75", "#0e7490"],
    spineTrim: "#cfd8dc",
    spineLabel: "#e8f1f4",
    spineLabelClass: "font-sans tracking-wide",
    coverFrame: "rounded-sm ring-1 ring-water/60",
    vinylDisc: "#0b1626",
    vinylLabels: ["#60a5fa", "#3b82f6", "#22d3ee", "#93c5fd"],
  },
  // Spirit / aether reads as `glas` — a celtic teal (grey-green-blue), distinct from water's azure. Purple is
  // reserved for the `illumination` semantic seam, not this element.
  spirit: {
    label: "Spirit",
    swatch: "#14b8a6",
    spinePalette: ["#0d9488", "#0f766e", "#115e59", "#134e4a", "#14b8a6", "#0e7490"],
    spineTrim: "#99f6e4",
    spineLabel: "#f0fdfa",
    spineLabelClass: "font-serif tracking-wider",
    coverFrame: "rounded-sm ring-1 ring-spirit/70 shadow-md",
    vinylDisc: "#04211d",
    vinylLabels: ["#14b8a6", "#2dd4bf", "#5eead4", "#0d9488"],
  },
  // Illumination: a user-facing theme — violet with gilt (gold) trim, evoking an illuminated manuscript.
  // Shares violet with the `suggestion` system token today but is its own theme colour.
  illumination: {
    label: "Illumination",
    swatch: "#8b5cf6",
    spinePalette: ["#4c1d95", "#5b21b6", "#6d28d9", "#7c3aed", "#3730a3", "#4338ca"],
    spineTrim: "var(--color-gold, #e0b74a)",
    spineLabel: "#ede9fe",
    spineLabelClass: "font-serif tracking-wider",
    coverFrame: "rounded-sm p-0.5 shadow-md ring-1 ring-illumination/70",
    vinylDisc: "#140a24",
    vinylLabels: ["#8b5cf6", "#a78bfa", "#c4b5fd", "#7c3aed"],
  },
  // Gold: the warm gilt/leather library look the old "spirit" (amber) used to carry.
  gold: {
    label: "Gold",
    swatch: "#fcd34d",
    spinePalette: ["#5a3821", "#6b2f2f", "#4a2f1a", "#734321", "#3f2415", "#5c4326"],
    spineTrim: "var(--color-gold, #e0b74a)",
    spineLabel: "#f4e4c1",
    spineLabelClass: "font-serif tracking-wider",
    coverFrame: "rounded-sm p-0.5 shadow-md ring-1 ring-gold/60",
    vinylDisc: "#181514",
    vinylLabels: ["#fcd34d", "#fbbf24", "#f59e0b", "#e0b74a"],
  },
  // Mono: black & white. Near-black spines, bright label, silver trim; grayscale vinyl.
  mono: {
    label: "Mono",
    swatch: "#111827",
    spinePalette: ["#111827", "#1f2937", "#0f172a", "#374151", "#1e293b", "#0b0f19"],
    spineTrim: "#e5e7eb",
    spineLabel: "#f9fafb",
    spineLabelClass: "font-sans tracking-wide uppercase",
    coverFrame: "rounded-sm ring-1 ring-black/20 dark:ring-white/20",
    vinylDisc: "#0a0a0a",
    vinylLabels: ["#e5e7eb", "#9ca3af", "#f9fafb", "#d1d5db"],
    vinylGroove: "rgba(255,255,255,0.12)",
  },
}

// A representative swatch colour per element, for the theme picker (name -> hex).
export const ELEMENT_SWATCHES: Record<ElementThemeName, string> = Object.fromEntries(
  ELEMENT_THEME_NAMES.map((name) => [name, ELEMENTS[name].swatch])
) as Record<ElementThemeName, string>

// A human label per element, for the picker tooltip.
export const ELEMENT_LABELS: Record<ElementThemeName, string> = Object.fromEntries(
  ELEMENT_THEME_NAMES.map((name) => [name, ELEMENTS[name].label])
) as Record<ElementThemeName, string>

// A distinct colour NAME for elements whose colour has its own identity beyond the element label (shown in
// the picker tooltip as e.g. "Glas · Spirit"). Only listed where it differs from the plain label.
export const ELEMENT_COLOR_NAMES: Partial<Record<ElementThemeName, string>> = {
  spirit: "Glas",
}

// The three per-renderer registries, built from the one source above.
export const SPINE_ELEMENT_THEMES: Record<ElementThemeName, BookSpineTheme> =
  Object.fromEntries(
    ELEMENT_THEME_NAMES.map((name) => {
      const e = ELEMENTS[name]
      return [
        name,
        {
          palette: e.spinePalette,
          trim: e.spineTrim,
          labelColor: e.spineLabel,
          labelClassName: e.spineLabelClass,
        } satisfies BookSpineTheme,
      ]
    })
  ) as Record<ElementThemeName, BookSpineTheme>

export const COVER_ELEMENT_THEMES: Record<ElementThemeName, BookCoverTheme> =
  Object.fromEntries(
    ELEMENT_THEME_NAMES.map((name) => [
      name,
      {
        frameClassName: ELEMENTS[name].coverFrame,
        // Coverless fallback uses the SAME element's spine, so a shelf reads consistently.
        spineTheme: name,
      } satisfies BookCoverTheme,
    ])
  ) as Record<ElementThemeName, BookCoverTheme>

export const VINYL_ELEMENT_THEMES: Record<ElementThemeName, SongVinylTheme> =
  Object.fromEntries(
    ELEMENT_THEME_NAMES.map((name) => {
      const e = ELEMENTS[name]
      return [
        name,
        {
          disc: e.vinylDisc,
          labels: e.vinylLabels,
          groove: e.vinylGroove ?? DEFAULT_GROOVE,
          sleeveClass: ELEMENT_BG[name],
        } satisfies SongVinylTheme,
      ]
    })
  ) as Record<ElementThemeName, SongVinylTheme>
